// Shared address vocabulary for every address field in the system.
// Pure data only: no Firebase, no DOM, no i18n side effects.
export const CUSTOM_ADDRESS_VALUE = '__custom__';

const GROUP_ORDER = Object.freeze({ customer: 0, terminal: 1, other: 2 });

export function normalizeAddressText(value) {
  return String(value ?? '').replace(/\s+/gu, ' ').trim();
}

function matchKey(value) {
  return normalizeAddressText(value).toLocaleLowerCase('en');
}

function plainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function sourceAddress(source) {
  if (typeof source === 'string') return normalizeAddressText(source);
  if (!plainObject(source)) return '';
  return normalizeAddressText(
    source.address
      ?? source.delivery_warehouse_address
      ?? source.address_text
      ?? source.value,
  );
}

function sourceLabel(source, address) {
  if (!plainObject(source)) return address;
  const label = normalizeAddressText(
    source.label
      ?? source.selection_label
      ?? source.name
      ?? source.code,
  );
  return label || address;
}

// The customer a quote address belongs to travels with the option, so an address
// dropdown can be narrowed to the row's own customer instead of offering every
// customer's warehouses at once (Alan, 2026-08-01). Terminals and extras carry no
// customer and stay visible to everyone.
export function customerMatchKey(value) {
  return matchKey(value);
}

function catalogEntries(sources, group) {
  return (Array.isArray(sources) ? sources : []).flatMap((source) => {
    const address = sourceAddress(source);
    if (!address) return [];
    const customerName = plainObject(source)
      ? normalizeAddressText(source.customer_name ?? source.customerName) : '';
    return [Object.freeze({
      value: address,
      label: sourceLabel(source, address),
      address,
      group,
      customerName,
      customerKey: customerName ? matchKey(customerName) : '',
      quoteDisplay: plainObject(source) ? String(source.quote_display ?? '') : '',
      baseQuoteCents: plainObject(source) && Number.isFinite(Number(source.base_quote_cents))
        ? Number(source.base_quote_cents) : null,
    })];
  });
}

function compareOptions(left, right) {
  return (GROUP_ORDER[left.group] ?? GROUP_ORDER.other) - (GROUP_ORDER[right.group] ?? GROUP_ORDER.other)
    || left.label.localeCompare(right.label, 'en', { sensitivity: 'base' })
    || left.address.localeCompare(right.address, 'en', { sensitivity: 'base' });
}

export function buildAddressCatalog({ quoteLocations, terminals, extras } = {}) {
  const merged = new Map();
  [
    ...catalogEntries(quoteLocations, 'customer'),
    ...catalogEntries(terminals, 'terminal'),
    ...catalogEntries(extras, 'other'),
  ].forEach((option) => {
    const key = matchKey(option.address);
    if (merged.has(key)) return;
    merged.set(key, option);
  });
  const options = Object.freeze([...merged.values()].sort(compareOptions));
  const index = new Map(options.map((option) => [matchKey(option.address), option]));
  const find = (addressText) => {
    const key = matchKey(addressText);
    return key ? index.get(key) ?? null : null;
  };
  return Object.freeze({
    options,
    has: (addressText) => find(addressText) !== null,
    find,
  });
}

export function isCustomAddress(catalog, addressText) {
  const address = normalizeAddressText(addressText);
  if (!address) return false;
  if (typeof catalog?.has !== 'function') return true;
  return catalog.has(address) !== true;
}
